import { config } from '@services/config';
import { useQuery } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';
import { omitBy, isNil } from 'lodash';

export interface CustomerLookupAddress {
  name: string;
  street1: string;
  street2: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface CustomerLookupParams {
  name: string;
  address: CustomerLookupAddress;
}

export interface CustomerLookupItem {
  id: string;
  mdmCustomerId: string;
  name: string;
  street1: string;
  city: string;
  state: string;
  zipCode: string;
  score: number;
}

export const useCustomerLookup = (params: CustomerLookupParams, enabled: boolean) => {
  const client = useClient();

  const queryFn = async () => {
    const queryParams = omitBy(
      {
        name: params.name,
        street1: params.address.street1,
        street2: params.address.street2,
        city: params.address.city,
        state: params.address.state,
        zipCode: params.address.zipCode,
      },
      isNil,
    ) as Record<string, string>;
    const queryString = new URLSearchParams(queryParams);
    const response = await client.get(`customer/lookup/mdm?${queryString}`).json<CustomerLookupItem[]>();
    const result = response || [];
    return result;
  };

  const result = useQuery({
    queryKey: ['customerLookup', params],
    queryFn,
    enabled,
    ...config,
  });

  return {
    ...result,
    performCustomerLookup: result.refetch,
    lookupResults: result.data || [],
  };
};
